import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Descriptions,
  Drawer,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, ScanOutlined, RotateRightOutlined } from "@ant-design/icons";
import { matriculaAPI, documentAPI } from "../services/api";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://comarca.5bc.us/api";

function documentPdfUrl(documentId?: number | null, cacheBust?: number | string): string {
  if (!documentId) return "";
  const base = `${API_BASE_URL}/documents/download/${documentId}`;
  return cacheBust != null ? `${base}?t=${cacheBust}` : base;
}

export type MatriculaRow = {
  matricula_id: number;
  case_id: number;
  document_id?: number;
  rol?: string;
  conjunto?: string;
  manzana?: string;
  lote?: string;
  matricula?: string;
  superficie_m2?: string;
  superficie_restante_m2?: string;
  tipo_predio?: string;
  ubicacion?: string;
  titular_asiento_1?: string;
  ci_titular?: string;
  fecha_nacimiento?: string;
  estado_civil?: string;
  nacionalidad?: string;
  acto_asiento_1?: string;
  escritura_nro?: string;
  escritura_fecha?: string;
  notario?: string;
  notaria?: string;
  presentacion_nro?: string;
  presentacion_fecha?: string;
  presentacion_hora?: string;
  timbre_fecha_hora?: string;
  anotacion_preventiva?: string;
  prohibicion?: string;
  fecha_medida?: string;
  expediente_nurej?: string;
  gravamenes_resumen?: string;
  cancelaciones_resumen?: string;
  presentacion_preventiva_nro?: string;
  presentacion_preventiva_fecha?: string;
  presentacion_preventiva_hora?: string;
  archivo?: string;
  asientos?: any[];
};

/** Filename → urbanización / parcela (shown in Rol column). */
export function conjuntoFromArchivo(archivo?: string): string {
  const raw = (archivo || "").trim();
  const name = raw.includes("/") ? raw.split("/").pop() || raw : raw;

  let m = name.match(/^R\s*(\d{1,2})\b/i);
  if (m && +m[1] >= 1 && +m[1] <= 20) return "Vergel de Jumilla";

  m = name.match(/^J\s*(\d{1,2})\b/i);
  if (m && +m[1] >= 1 && +m[1] <= 18) return "Bosque de Loreto";

  m = name.match(/^N\s*(\d{1,2})\b/i);
  if (m && +m[1] >= 1 && +m[1] <= 8) return "Campiña de Guardiola";

  m = name.match(/MZ[Aa]?[\s-]*(\d+)/i);
  if (m) {
    const known: Record<string, string> = {
      "14": "Parcela 14",
      "20": "Parcela 20",
      "25": "Parcela 25",
      "26": "Parcela 26",
    };
    return known[m[1]] || `Parcela ${m[1]}`;
  }
  return "";
}

function labelConjunto(r: MatriculaRow): string {
  return (r.conjunto || conjuntoFromArchivo(r.archivo) || "").trim();
}

const CONJUNTO_COLOR: Record<string, string> = {
  "Vergel de Jumilla": "geekblue",
  "Bosque de Loreto": "green",
  "Campiña de Guardiola": "cyan",
  "Parcela 14": "gold",
  "Parcela 20": "orange",
  "Parcela 25": "purple",
  "Parcela 26": "magenta",
};

/** These conjuntos collapse to a single top-level row. */
const CONJUNTOS_AGRUPADOS = new Set([
  "Vergel de Jumilla",
  "Bosque de Loreto",
  "Campiña de Guardiola",
]);

export type SortMode = "matricula" | "propietario" | "lote" | "caso" | "conjunto";

const SORT_OPTIONS_TOP: { label: string; value: SortMode }[] = [
  { label: "Rol / conjunto", value: "conjunto" },
  { label: "Caso", value: "caso" },
];

const SORT_OPTIONS_LIST: { label: string; value: SortMode }[] = [
  { label: "Lote", value: "lote" },
  { label: "Matrícula", value: "matricula" },
  { label: "Propietario", value: "propietario" },
];

type TopRow =
  | {
      key: string;
      kind: "grupo";
      conjunto: string;
      case_id: number;
      members: MatriculaRow[];
      suma_m2: number;
      n_aranibar: number;
    }
  | {
      key: string;
      kind: "madre";
      madre: MatriculaRow;
      n_hijas: number;
      n_bandera: number;
    };

function cmpStr(a?: string | null, b?: string | null): number {
  return (a || "").localeCompare(b || "", "es", { sensitivity: "base", numeric: true });
}

function sortRows(rows: MatriculaRow[], mode: SortMode): MatriculaRow[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    let primary = 0;
    if (mode === "matricula") primary = cmpStr(a.matricula, b.matricula);
    else if (mode === "propietario") primary = cmpStr(a.titular_asiento_1, b.titular_asiento_1);
    else if (mode === "conjunto") primary = cmpStr(labelConjunto(a), labelConjunto(b));
    else if (mode === "lote") {
      const parseLote = (s?: string | null) => {
        const t = (s || "").trim();
        const m = t.match(/^([RJN])\s*(\d+)$/i);
        if (m) return { letter: m[1].toUpperCase(), n: parseInt(m[2], 10) };
        const n = parseInt(t, 10);
        return { letter: "", n: Number.isNaN(n) ? NaN : n };
      };
      const la = parseLote(a.lote);
      const lb = parseLote(b.lote);
      if (la.letter && lb.letter && la.letter === lb.letter && !Number.isNaN(la.n) && !Number.isNaN(lb.n)) {
        primary = la.n - lb.n;
      } else if (!Number.isNaN(la.n) && !Number.isNaN(lb.n) && !la.letter && !lb.letter) {
        primary = la.n - lb.n;
      } else {
        primary = cmpStr(a.lote, b.lote);
      }
    } else primary = (a.case_id || 0) - (b.case_id || 0);
    if (primary !== 0) return primary;
    const t = cmpStr(a.titular_asiento_1, b.titular_asiento_1);
    if (t !== 0) return t;
    return cmpStr(a.matricula, b.matricula);
  });
  return copy;
}

function matchesQuery(r: MatriculaRow, q: string): boolean {
  if (!q) return true;
  const blob = [
    r.matricula,
    r.titular_asiento_1,
    r.ci_titular,
    r.lote,
    r.manzana,
    r.archivo,
    r.anotacion_preventiva,
    r.presentacion_preventiva_nro,
    r.gravamenes_resumen,
    r.timbre_fecha_hora,
    labelConjunto(r),
    String(r.case_id),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return blob.includes(q);
}

function hijasDeMadre(madre: MatriculaRow, all: MatriculaRow[]): MatriculaRow[] {
  return all.filter((r) => {
    if ((r.rol || "") !== "hija") return false;
    if (r.case_id !== madre.case_id) return false;
    if (madre.manzana && r.manzana) return r.manzana === madre.manzana;
    return true;
  });
}

/** Timbre electrónico → epoch ms (0 if missing/invalid). */
function timbreSortKey(t?: string | null): number {
  if (!t) return 0;
  const n = Date.parse(String(t).trim().replace(" ", "T"));
  return Number.isNaN(n) ? 0 : n;
}

/** Prefer folio with latest electronic stamp; else Alodial; else one with m². */
function preferHija(a: MatriculaRow, b: MatriculaRow): MatriculaRow {
  const ta = timbreSortKey(a.timbre_fecha_hora);
  const tb = timbreSortKey(b.timbre_fecha_hora);
  if (ta !== tb) return ta > tb ? a : b;
  const aAlod = /alodial/i.test(a.archivo || "");
  const bAlod = /alodial/i.test(b.archivo || "");
  if (aAlod !== bAlod) return aAlod ? a : b;
  if (!!(a.superficie_m2 || "").trim() !== !!(b.superficie_m2 || "").trim()) {
    return (a.superficie_m2 || "").trim() ? a : b;
  }
  return a;
}

/**
 * Exactly one hija per lote — the folio with the most recent timbre electrónico.
 * Avoids double-counting Alodial + MZ-26 N of the same lot (sum must match madre).
 */
function hijasUnicasPorLote(madre: MatriculaRow, all: MatriculaRow[]): MatriculaRow[] {
  const hijas = hijasDeMadre(madre, all);
  const byLote = new Map<string, MatriculaRow>();
  const sinLote: MatriculaRow[] = [];
  for (const h of hijas) {
    const key = (h.lote || "").trim();
    if (!key) {
      sinLote.push(h);
      continue;
    }
    const prev = byLote.get(key);
    byLote.set(key, prev ? preferHija(prev, h) : h);
  }
  const unicas = [...byLote.values()].sort((a, b) => {
    const la = parseInt(a.lote || "", 10);
    const lb = parseInt(b.lote || "", 10);
    if (!Number.isNaN(la) && !Number.isNaN(lb)) return la - lb;
    return cmpStr(a.lote, b.lote);
  });
  return [...unicas, ...sinLote];
}

/**
 * Ids of hijas that carry the "Bandera" (most recent timbre electrónico per lote).
 * History stays visible; only these count toward the madre surface sum.
 */
function idsBanderaPorLote(madre: MatriculaRow, all: MatriculaRow[]): Set<number> {
  return new Set(hijasUnicasPorLote(madre, all).map((h) => h.matricula_id));
}

function idMatriculaMasReciente(list: MatriculaRow[]): number | null {
  let bestId: number | null = null;
  let bestT = 0;
  for (const r of list) {
    const t = timbreSortKey(r.timbre_fecha_hora);
    if (t > bestT) {
      bestT = t;
      bestId = r.matricula_id;
    }
  }
  return bestT > 0 ? bestId : null;
}

function sumM2(list: MatriculaRow[]): number {
  return list.reduce((s, h) => s + (parseFloat(h.superficie_m2 || "0") || 0), 0);
}

function ArchivoLink({
  row,
  onOpen,
}: {
  row: MatriculaRow;
  onOpen: (row: MatriculaRow) => void;
}) {
  const name = row.archivo || "";
  if (!name) return <span>—</span>;
  if (!row.document_id) return <span title="Sin document_id">{name}</span>;
  return (
    <a
      href={documentPdfUrl(row.document_id)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(row);
      }}
      title={`Ver ${name}`}
      style={{ color: "#1677ff" }}
    >
      <FilePdfOutlined style={{ marginRight: 4 }} />
      {name}
    </a>
  );
}

function DetailBlock({
  row,
  onOpenPdf,
  onUpdated,
}: {
  row: MatriculaRow;
  onOpenPdf: (row: MatriculaRow) => void;
  onUpdated?: (row: MatriculaRow) => void;
}) {
  const [reocrLoading, setReocrLoading] = useState(false);
  const [uprightLoading, setUprightLoading] = useState(false);
  const sinMatricula = !(row.matricula || "").trim();
  const sinSuperficie = !(row.superficie_m2 || "").trim();

  const handleReocrHd = async () => {
    setReocrLoading(true);
    const hide = message.loading(
      "Re-escaneando PDF a alta resolución (puede tardar 1–3 min)…",
      0
    );
    try {
      const res = await matriculaAPI.reocrHd(row.matricula_id, {
        dpi: 450,
        max_pages: 2,
      });
      hide();
      message.success(res.mensaje || "OCR HD listo");
      // Prefer server superficie; overrides keep Parcela/Mz 20 at 12232.34
      const updated = { ...row, ...(res.matricula || {}) };
      onUpdated?.(updated);
      // Refresh parent list if surface changed
      if (
        updated.superficie_m2 &&
        updated.superficie_m2 !== row.superficie_m2
      ) {
        message.info(`Superficie: ${updated.superficie_m2} m²`);
      }
    } catch (e: any) {
      hide();
      message.error(
        e?.response?.data?.detail || e?.message || "Falló el re-escaneo HD"
      );
    } finally {
      setReocrLoading(false);
    }
  };

  const handleUpright = async () => {
    if (!row.document_id) {
      message.warning("Sin document_id para enderezar el PDF");
      return;
    }
    if (uprightLoading) return;
    setUprightLoading(true);
    const hide = message.loading("Guardando PDF horizontal…", 0);
    try {
      // Orientation only (fast). Use "Re-escanear HD" if fields need refresh.
      const res = await documentAPI.uprightPdf(row.document_id, false);
      hide();
      if (!res.changed) {
        message.info(res.message || "El PDF ya estaba horizontal");
      } else {
        message.success(res.message || "PDF guardado horizontal");
      }
      onOpenPdf({ ...row });
    } catch (e: any) {
      hide();
      message.error(
        e?.response?.data?.detail || e?.message || "No se pudo guardar horizontal"
      );
    } finally {
      setUprightLoading(false);
    }
  };

  return (
    <>
      {(sinMatricula || sinSuperficie) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            sinMatricula
              ? "No se leyó el número de matrícula"
              : "No se leyó la superficie (m²)"
          }
          description="Si el PDF está de costado, guárdelo horizontal y vuelva a extraer. También puede re-escanear en HD."
          action={
            <Space direction="vertical" size={4}>
              <Button
                size="small"
                type="primary"
                icon={<RotateRightOutlined />}
                loading={uprightLoading}
                onClick={() => void handleUpright()}
              >
                Guardar horizontal
              </Button>
              <Button
                size="small"
                icon={<ScanOutlined />}
                loading={reocrLoading}
                onClick={() => void handleReocrHd()}
              >
                Re-escanear HD
              </Button>
            </Space>
          }
        />
      )}
      <Space wrap style={{ marginBottom: 12 }}>
        <Button
          icon={<RotateRightOutlined />}
          loading={uprightLoading}
          onClick={() => void handleUpright()}
        >
          Guardar horizontal
        </Button>
        <Button
          icon={<ScanOutlined />}
          loading={reocrLoading}
          onClick={() => void handleReocrHd()}
        >
          Re-escanear HD
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Endereza el PDF en disco · OCR HD activo (450 DPI)
        </Typography.Text>
      </Space>
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Rol / conjunto">
          {labelConjunto(row) || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Matrícula">
          {row.matricula ? (
            row.matricula
          ) : (
            <Tag color="red">sin leer</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Propietario">{row.titular_asiento_1 || "—"}</Descriptions.Item>
        <Descriptions.Item label="C.I.">{row.ci_titular || "—"}</Descriptions.Item>
        <Descriptions.Item label="Superficie">
          {row.superficie_m2 || "—"} m²
          {row.superficie_restante_m2 ? ` (restante ${row.superficie_restante_m2})` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Ubicación">{row.ubicacion || "—"}</Descriptions.Item>
        <Descriptions.Item label="Acto">{row.acto_asiento_1 || "—"}</Descriptions.Item>
        <Descriptions.Item label="Escritura">
          {row.escritura_nro
            ? `${row.escritura_nro} de ${row.escritura_fecha || ""}`
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Presentación dominio">
          {row.presentacion_nro
            ? `${row.presentacion_nro} · ${row.presentacion_fecha || ""} ${row.presentacion_hora || ""}`
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Timbre electrónico">
          {row.timbre_fecha_hora || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Preventiva">{row.anotacion_preventiva || "—"}</Descriptions.Item>
        <Descriptions.Item label="Prohibición">{row.prohibicion || "—"}</Descriptions.Item>
        <Descriptions.Item label="Present. preventiva">
          {row.presentacion_preventiva_nro
            ? `${row.presentacion_preventiva_nro} · ${row.presentacion_preventiva_fecha || ""} ${row.presentacion_preventiva_hora || ""}`
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Gravámenes">{row.gravamenes_resumen || "—"}</Descriptions.Item>
        <Descriptions.Item label="Cancelaciones">{row.cancelaciones_resumen || "—"}</Descriptions.Item>
        <Descriptions.Item label="Archivo">
          <ArchivoLink row={row} onOpen={onOpenPdf} />
        </Descriptions.Item>
      </Descriptions>
      {(row.asientos || []).length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Gravámenes y restricciones (Asientos B / C)
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
            Nombre completo, documento de identidad y fecha de presentación de cada
            asiento del folio real.
          </Typography.Paragraph>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 1100 }}
            rowKey={(a: any) => a.asiento_id}
            dataSource={(row.asientos || []).filter(
              (a: any) => a.tipo !== "Sin asientos"
            )}
            expandable={{
              expandedRowRender: (a: any) => (
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Sección">{a.seccion || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Personas / sujeto">
                    <div style={{ whiteSpace: "pre-wrap" }}>{a.sujeto || "—"}</div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Documento / C.I.">
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {a.identificacion || "—"}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Documento registral">
                    {a.documento || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Autoridad / juez">
                    {a.autoridad || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Juzgado">{a.juzgado || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Expediente">
                    {a.expediente || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Restricción">
                    {a.restriccion || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Presentación">
                    {a.presentacion_nro
                      ? `${a.presentacion_nro} · ${a.presentacion_fecha || ""} ${
                          a.presentacion_hora || ""
                        }`
                      : "—"}
                  </Descriptions.Item>
                  {a.efecto ? (
                    <Descriptions.Item label="Efecto">{a.efecto}</Descriptions.Item>
                  ) : null}
                </Descriptions>
              ),
            }}
            columns={[
              { title: "N°", dataIndex: "asiento_nro", width: 50 },
              {
                title: "Tipo",
                dataIndex: "tipo",
                width: 130,
                render: (t: string) =>
                  t === "Gravamen" ? (
                    <Tag color="purple">{t}</Tag>
                  ) : t === "Anotación preventiva" ? (
                    <Tag color="orange">{t}</Tag>
                  ) : (
                    t || "—"
                  ),
              },
              {
                title: "Descripción",
                dataIndex: "descripcion",
                width: 200,
                ellipsis: true,
              },
              {
                title: "Nombre completo",
                dataIndex: "sujeto",
                width: 220,
                render: (t: string) => (
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                    {t || "—"}
                  </div>
                ),
              },
              {
                title: "Doc / C.I.",
                dataIndex: "identificacion",
                width: 140,
                render: (t: string) => (
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                    {t || "—"}
                  </div>
                ),
              },
              {
                title: "Presentación",
                key: "present",
                width: 180,
                render: (_: any, a: any) =>
                  a.presentacion_nro ? (
                    <span style={{ fontSize: 12 }}>
                      {a.presentacion_nro}
                      <br />
                      {a.presentacion_fecha || ""} {a.presentacion_hora || ""}
                    </span>
                  ) : (
                    "—"
                  ),
              },
              {
                title: "Restricción",
                dataIndex: "restriccion",
                width: 160,
                ellipsis: true,
                render: (t: string) =>
                  t ? <Tag color="volcano">{t.replace("PROHIBICIÓN DE ", "")}</Tag> : "—",
              },
            ]}
          />
        </>
      )}
    </>
  );
}

function ParcelasTable({
  list,
  loading,
  onSelect,
  onOpenPdf,
  idsBandera,
  idMasReciente,
}: {
  list: MatriculaRow[];
  loading?: boolean;
  onSelect: (r: MatriculaRow) => void;
  onOpenPdf: (r: MatriculaRow) => void;
  /** Folios with Bandera = latest timbre per lote (history kept, these are current). */
  idsBandera?: Set<number> | null;
  idMasReciente?: number | null;
}) {
  const hasBandera = (id: number) => !!(idsBandera && idsBandera.has(id));
  return (
    <Table
      rowKey="matricula_id"
      loading={loading}
      dataSource={list}
      scroll={{ x: 1200, y: "calc(100vh - 380px)" }}
      pagination={{ pageSize: 25, showSizeChanger: true }}
      size="middle"
      onRow={(record) => ({
        onClick: () => onSelect(record),
        style: {
          cursor: "pointer",
          background: hasBandera(record.matricula_id)
            ? "rgba(82, 196, 26, 0.08)"
            : undefined,
        },
      })}
      locale={{ emptyText: "Sin matrículas en este conjunto" }}
      columns={[
        { title: "Lote", dataIndex: "lote", width: 70 },
        {
          title: "Matrícula",
          dataIndex: "matricula",
          width: 220,
          render: (t: string, r: MatriculaRow) => (
            <span>
              {t || <Tag color="red">sin leer</Tag>}
              {hasBandera(r.matricula_id) ? (
                <Tag color="green" style={{ marginLeft: 6 }}>
                  Bandera
                </Tag>
              ) : null}
              {idMasReciente && r.matricula_id === idMasReciente ? (
                <Tag color="blue" style={{ marginLeft: 4 }}>
                  más reciente
                </Tag>
              ) : null}
            </span>
          ),
        },
        { title: "m²", dataIndex: "superficie_m2", width: 90 },
        {
          title: "Timbre electrónico",
          dataIndex: "timbre_fecha_hora",
          width: 175,
          render: (t: string) => t || "—",
        },
        {
          title: "Propietario",
          dataIndex: "titular_asiento_1",
          width: 220,
          ellipsis: true,
          render: (t: string) =>
            /ARANIBAR\s+CUELLAR\s+ROSSEMARY/i.test(t || "") ? (
              <span>
                <Tag color="purple">Aranibar</Tag> {t}
              </span>
            ) : (
              t || "—"
            ),
        },
        {
          title: "Preventiva",
          dataIndex: "anotacion_preventiva",
          width: 170,
          ellipsis: true,
          render: (t: string) =>
            t ? (
              <Tag color="orange">{t.replace("Anotación Preventiva: ", "")}</Tag>
            ) : (
              "—"
            ),
        },
        {
          title: "Present. prev.",
          key: "pp",
          width: 160,
          render: (_: any, r: MatriculaRow) =>
            r.presentacion_preventiva_nro
              ? `${r.presentacion_preventiva_nro} · ${r.presentacion_preventiva_fecha || ""}`
              : "—",
        },
        {
          title: "Cancelaciones",
          dataIndex: "cancelaciones_resumen",
          width: 140,
          ellipsis: true,
        },
        {
          title: "Archivo",
          dataIndex: "archivo",
          width: 150,
          ellipsis: true,
          render: (_: string, r: MatriculaRow) => (
            <ArchivoLink row={r} onOpen={onOpenPdf} />
          ),
        },
      ]}
    />
  );
}

type Props = {
  rows: MatriculaRow[];
  loading?: boolean;
  showCaseColumn?: boolean;
  emptyText?: string;
  onMatriculaUpdated?: (row: MatriculaRow) => void;
};

export default function MatriculaPlanillaTable({
  rows,
  loading,
  showCaseColumn = true,
  emptyText,
  onMatriculaUpdated,
}: Props) {
  const [sortTop, setSortTop] = useState<SortMode>("conjunto");
  const [sortList, setSortList] = useState<SortMode>("matricula");
  const [query, setQuery] = useState("");
  const [grupoActivo, setGrupoActivo] = useState<{
    conjunto: string;
    members: MatriculaRow[];
  } | null>(null);
  const [madreActiva, setMadreActiva] = useState<MatriculaRow | null>(null);
  const [detalle, setDetalle] = useState<MatriculaRow | null>(null);
  const [verDetalleMadre, setVerDetalleMadre] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{
    url: string;
    title: string;
    documentId?: number;
    matriculaId?: number;
  } | null>(null);
  const [pdfUprightLoading, setPdfUprightLoading] = useState(false);

  const openPdf = (row: MatriculaRow) => {
    const url = documentPdfUrl(row.document_id, Date.now());
    if (!url) return;
    setPdfPreview({
      url,
      title: row.archivo || row.matricula || `Doc #${row.document_id}`,
      documentId: row.document_id,
      matriculaId: row.matricula_id,
    });
  };

  const applyUpdatedRow = (updated: MatriculaRow) => {
    onMatriculaUpdated?.(updated);
    setDetalle((prev) =>
      prev && prev.matricula_id === updated.matricula_id ? { ...prev, ...updated } : prev
    );
    setMadreActiva((prev) =>
      prev && prev.matricula_id === updated.matricula_id ? { ...prev, ...updated } : prev
    );
    setGrupoActivo((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        members: prev.members.map((m) =>
          m.matricula_id === updated.matricula_id ? { ...m, ...updated } : m
        ),
      };
    });
  };

  const pdfModal = (
    <Modal
      open={!!pdfPreview}
      onCancel={() => setPdfPreview(null)}
      footer={
        pdfPreview ? (
          <Space>
            {pdfPreview.documentId ? (
              <Button
                icon={<RotateRightOutlined />}
                loading={pdfUprightLoading}
                disabled={pdfUprightLoading}
                onClick={async () => {
                  if (pdfUprightLoading) return;
                  setPdfUprightLoading(true);
                  const hide = message.loading("Guardando PDF horizontal…", 0);
                  try {
                    const res = await documentAPI.uprightPdf(
                      pdfPreview.documentId!,
                      false
                    );
                    hide();
                    message.success(
                      res.changed
                        ? res.message || "PDF guardado horizontal"
                        : res.message || "Ya estaba horizontal"
                    );
                    setPdfPreview({
                      ...pdfPreview,
                      url: documentPdfUrl(pdfPreview.documentId, Date.now()),
                    });
                  } catch (e: any) {
                    hide();
                    message.error(
                      e?.response?.data?.detail ||
                        e?.message ||
                        "No se pudo enderezar"
                    );
                  } finally {
                    setPdfUprightLoading(false);
                  }
                }}
              >
                Guardar horizontal
              </Button>
            ) : null}
            <Button href={pdfPreview.url} target="_blank" rel="noreferrer">
              Abrir en pestaña
            </Button>
            <Button type="primary" onClick={() => setPdfPreview(null)}>
              Cerrar
            </Button>
          </Space>
        ) : null
      }
      width="90vw"
      style={{ top: 24 }}
      title={pdfPreview ? `PDF — ${pdfPreview.title}` : "PDF"}
      destroyOnClose
    >
      {pdfPreview ? (
        <iframe
          title="planilla-pdf-view"
          src={pdfPreview.url}
          style={{ width: "100%", height: "78vh", border: "1px solid #d9d9d9" }}
        />
      ) : null}
    </Modal>
  );

  const q = query.trim().toLowerCase();

  const topRows: TopRow[] = useMemo(() => {
    const madres = rows.filter((r) => (r.rol || "").toLowerCase() !== "hija");
    const filtered = madres.filter((m) => {
      if (!q) return true;
      if (matchesQuery(m, q)) return true;
      return hijasDeMadre(m, rows).some((h) => matchesQuery(h, q));
    });

    const byGrupo = new Map<string, MatriculaRow[]>();
    const individuales: MatriculaRow[] = [];

    for (const m of filtered) {
      const c = labelConjunto(m);
      if (c && CONJUNTOS_AGRUPADOS.has(c)) {
        const list = byGrupo.get(c) || [];
        list.push(m);
        byGrupo.set(c, list);
      } else {
        individuales.push(m);
      }
    }

    const out: TopRow[] = [];
    for (const [conjunto, members] of byGrupo.entries()) {
      const sorted = sortRows(members, "matricula");
      out.push({
        key: `grupo-${conjunto}`,
        kind: "grupo",
        conjunto,
        case_id: sorted[0]?.case_id || 0,
        members: sorted,
        suma_m2: sumM2(sorted),
        n_aranibar: sorted.filter((x) =>
          /ARANIBAR\s+CUELLAR\s+ROSSEMARY/i.test(x.titular_asiento_1 || "")
        ).length,
      });
    }
    for (const m of individuales) {
      out.push({
        key: `madre-${m.matricula_id}`,
        kind: "madre",
        madre: m,
        n_hijas: hijasDeMadre(m, rows).length,
        n_bandera: hijasUnicasPorLote(m, rows).length,
      });
    }

    out.sort((a, b) => {
      if (sortTop === "caso") {
        const ca = a.kind === "grupo" ? a.case_id : a.madre.case_id;
        const cb = b.kind === "grupo" ? b.case_id : b.madre.case_id;
        return ca - cb;
      }
      const la = a.kind === "grupo" ? a.conjunto : labelConjunto(a.madre) || "ZZZ";
      const lb = b.kind === "grupo" ? b.conjunto : labelConjunto(b.madre) || "ZZZ";
      return cmpStr(la, lb);
    });
    return out;
  }, [rows, q, sortTop]);

  const listaDrill = useMemo(() => {
    if (grupoActivo) {
      let list = grupoActivo.members;
      if (q) list = list.filter((h) => matchesQuery(h, q));
      return sortRows(list, sortList);
    }
    if (madreActiva) {
      // Keep full history; Bandera marks latest timbre per lote
      let list = hijasDeMadre(madreActiva, rows);
      if (q) list = list.filter((h) => matchesQuery(h, q) || matchesQuery(madreActiva, q));
      return sortRows(list, sortList === "matricula" ? "lote" : sortList);
    }
    return [];
  }, [grupoActivo, madreActiva, rows, q, sortList]);

  const idsBandera = useMemo(() => {
    if (!madreActiva) return null;
    return idsBanderaPorLote(madreActiva, rows);
  }, [madreActiva, rows]);

  const idMasReciente = useMemo(() => {
    if (!madreActiva || !idsBandera) return idMatriculaMasReciente(listaDrill);
    // Most recent among Bandera folios only
    return idMatriculaMasReciente(
      listaDrill.filter((r) => idsBandera.has(r.matricula_id))
    );
  }, [listaDrill, madreActiva, idsBandera]);

  // Drill-down: grupo urbanización or parcela madre → hijas
  if (grupoActivo || madreActiva) {
    const titulo = grupoActivo
      ? grupoActivo.conjunto
      : `MZ ${madreActiva!.manzana || "—"} · ${madreActiva!.matricula || "sin código"}`;
    const color = grupoActivo
      ? CONJUNTO_COLOR[grupoActivo.conjunto]
      : CONJUNTO_COLOR[labelConjunto(madreActiva!)];

    const banderaRows = madreActiva
      ? listaDrill.filter((r) => idsBandera?.has(r.matricula_id))
      : listaDrill;
    const sumaBandera = sumM2(banderaRows);
    const madreM2 = madreActiva
      ? parseFloat(madreActiva.superficie_m2 || "0") || 0
      : 0;
    const delta = madreActiva ? Math.round((sumaBandera - madreM2) * 100) / 100 : 0;
    const sumaOk = madreActiva && madreM2 > 0 && Math.abs(delta) < 0.05;
    const masReciente = listaDrill.find((r) => r.matricula_id === idMasReciente);

    return (
      <div>
        <Space wrap style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                setGrupoActivo(null);
                setMadreActiva(null);
              }}
            >
              Volver
            </Button>
            <Breadcrumb
              items={[
                {
                  title: (
                    <a
                      onClick={() => {
                        setGrupoActivo(null);
                        setMadreActiva(null);
                      }}
                    >
                      Conjuntos
                    </a>
                  ),
                },
                { title: titulo },
              ]}
            />
          </Space>
          <Input.Search
            allowClear
            placeholder="Buscar matrícula, propietario…"
            style={{ minWidth: 260 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Space>

        <Alert
          type={madreActiva ? (sumaOk ? "success" : "warning") : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              <Tag color={color || "default"}>{titulo}</Tag>
              {grupoActivo ? (
                <>
                  {" "}
                  {listaDrill.length} matrículas · suma {sumM2(listaDrill).toFixed(2)} m²
                </>
              ) : (
                <>
                  {" "}
                  {madreActiva!.titular_asiento_1 || "sin titular"} ·{" "}
                  {madreActiva!.superficie_m2 || "—"} m²
                  {" · "}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setVerDetalleMadre(true)}
                    style={{ padding: 0 }}
                  >
                    ver detalle madre
                  </Button>
                </>
              )}
            </span>
          }
          description={
            madreActiva ? (
              <span>
                {listaDrill.length} folios en historial ·{" "}
                <Tag color="green">Bandera</Tag> {banderaRows.length} vigentes (timbre más
                reciente por lote) · suma Bandera{" "}
                <strong>{sumaBandera.toFixed(2)} m²</strong>
                {madreM2 > 0 ? (
                  <>
                    {" "}
                    · madre <strong>{madreM2.toFixed(2)} m²</strong>
                    {sumaOk ? (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        Bandera = madre
                      </Tag>
                    ) : (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        Δ {delta > 0 ? "+" : ""}
                        {delta.toFixed(2)} m²
                      </Tag>
                    )}
                  </>
                ) : null}
                {masReciente ? (
                  <>
                    <br />
                    <Tag color="blue">más reciente</Tag> lote {masReciente.lote || "—"} ·{" "}
                    {masReciente.matricula || "—"} · timbre {masReciente.timbre_fecha_hora}
                    {masReciente.archivo ? ` · ${masReciente.archivo}` : ""}
                  </>
                ) : null}
              </span>
            ) : (
              "Clic en una fila para ver el detalle del folio"
            )
          }
        />

        <Space wrap style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">Ordenar por</Typography.Text>
          <Segmented
            value={sortList}
            options={SORT_OPTIONS_LIST}
            onChange={(v) => setSortList(v as SortMode)}
          />
        </Space>

        <ParcelasTable
          list={listaDrill}
          loading={loading}
          onSelect={setDetalle}
          onOpenPdf={openPdf}
          idsBandera={idsBandera}
          idMasReciente={idMasReciente}
        />

        <Drawer
          title="Detalle matrícula madre"
          width={560}
          open={verDetalleMadre && !!madreActiva}
          onClose={() => setVerDetalleMadre(false)}
          destroyOnClose
        >
          {madreActiva && (
            <DetailBlock
              row={madreActiva}
              onOpenPdf={openPdf}
              onUpdated={applyUpdatedRow}
            />
          )}
        </Drawer>

        <Drawer
          title={
            detalle
              ? `${labelConjunto(detalle) || "Matrícula"} — ${detalle.matricula || ""}`
              : "Detalle"
          }
          width={560}
          open={!!detalle}
          onClose={() => setDetalle(null)}
          destroyOnClose
        >
          {detalle && (
            <DetailBlock
              row={detalle}
              onOpenPdf={openPdf}
              onUpdated={applyUpdatedRow}
            />
          )}
        </Drawer>

        {pdfModal}
      </div>
    );
  }

  // Top level: 1 row per urbanización agrupada + madres de parcelas
  return (
    <div>
      <Space wrap style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Space wrap>
          <Typography.Text type="secondary">Ordenar por</Typography.Text>
          <Segmented
            value={sortTop}
            options={SORT_OPTIONS_TOP}
            onChange={(v) => setSortTop(v as SortMode)}
          />
        </Space>
        <Input.Search
          allowClear
          placeholder="Buscar matrícula, propietario, conjunto…"
          style={{ minWidth: 280, maxWidth: 420 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Vergel, Bosque y Campiña: una línea por conjunto. Parcelas: madre → hijas (historial completo). La Bandera marca el timbre más reciente por lote; esa suma se compara con la madre."
      />

      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {topRows.length} conjunto{topRows.length === 1 ? "" : "s"} / madres
      </Typography.Text>

      <Table
        rowKey="key"
        loading={loading}
        dataSource={topRows}
        scroll={{ x: 1100, y: "calc(100vh - 360px)" }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        size="middle"
        onRow={(record) => ({
          onClick: () => {
            if (record.kind === "grupo") {
              setGrupoActivo({ conjunto: record.conjunto, members: record.members });
            } else {
              setMadreActiva(record.madre);
            }
          },
          style: { cursor: "pointer" },
        })}
        locale={{ emptyText: emptyText || "Sin matrículas" }}
        columns={[
          ...(showCaseColumn
            ? [
                {
                  title: "Caso",
                  key: "caso",
                  width: 80,
                  render: (_: any, r: TopRow) => {
                    const id = r.kind === "grupo" ? r.case_id : r.madre.case_id;
                    return (
                      <Link to={`/case/${id}`} onClick={(e) => e.stopPropagation()}>
                        {id}
                      </Link>
                    );
                  },
                } as any,
              ]
            : []),
          {
            title: "Rol",
            key: "rol",
            width: 190,
            render: (_: any, r: TopRow) => {
              const c = r.kind === "grupo" ? r.conjunto : labelConjunto(r.madre);
              return c ? (
                <Tag color={CONJUNTO_COLOR[c] || "default"}>{c}</Tag>
              ) : (
                <Tag>madre</Tag>
              );
            },
          },
          {
            title: "Matrículas",
            key: "n",
            width: 100,
            render: (_: any, r: TopRow) =>
              r.kind === "grupo" ? (
                <Tag color="blue">{r.members.length}</Tag>
              ) : (
                <span>
                  <Tag color="default">{1 + r.n_hijas}</Tag>
                  {r.n_bandera > 0 ? (
                    <Tag color="green">{r.n_bandera} Bandera</Tag>
                  ) : null}
                </span>
              ),
          },
          {
            title: "m² (suma)",
            key: "m2",
            width: 120,
            render: (_: any, r: TopRow) =>
              r.kind === "grupo"
                ? r.suma_m2.toFixed(2)
                : r.madre.superficie_m2 || "—",
          },
          {
            title: "Detalle",
            key: "det",
            ellipsis: true,
            render: (_: any, r: TopRow) => {
              if (r.kind === "grupo") {
                return r.n_aranibar
                  ? `${r.members.length} folios · ${r.n_aranibar} Aranibar`
                  : `${r.members.length} folios (R/J/N)`;
              }
              return (
                <>
                  {r.n_hijas} historial · {r.n_bandera} Bandera
                  {r.madre.titular_asiento_1
                    ? ` · ${r.madre.titular_asiento_1}`
                    : ""}
                </>
              );
            },
          },
          {
            title: "Archivos",
            key: "arch",
            width: 160,
            render: (_: any, r: TopRow) => {
              if (r.kind === "grupo") {
                const c = r.conjunto;
                if (c === "Vergel de Jumilla") return "R1–R20";
                if (c === "Bosque de Loreto") return "J1–J18";
                if (c === "Campiña de Guardiola") return "N1–N8";
                return "—";
              }
              return <ArchivoLink row={r.madre} onOpen={openPdf} />;
            },
          },
        ]}
      />
      {pdfModal}
    </div>
  );
}
